import { useState, useEffect, useMemo, useCallback } from "react";
import { url } from "../utils/config";
import Input from "../reusable_components/Input";
import { toast } from "react-toastify";
import Button from "../reusable_components/Button";
import {
  btnCancel,
  btnOne,
  btnOneNoWidth,
  inputStyle,
  label,
  modalContainer,
  modalTitle,
} from "../utils/style";
import Select from "../reusable_components/Select";
import axios from "axios";
import postData from "../custom_functions/postData";
import { MdDelete } from "react-icons/md";
import ConfirmationModal from "../reusable_components/ConfirmationModal";
import * as XLSX from "xlsx";
import ImportModal from "../reusable_components/ImportModal";
import debounce from "lodash.debounce";
import formatNumberToUs from "../custom_functions/formatNumberToUs";

function AddFleetCar({ viewData, setViewData, cars, importRowLimit }) {
  const [showModal, setShowModal] = useState(false);
  const [carData, setCarData] = useState([]);
  const [formData, setFormData] = useState({
    make: "",
    model: "",
    year: "",
    vin: "",
    plate: "",
  });
  const [missingFields, setMissingFields] = useState([]);
  const [inputErrors, setInputErrors] = useState([]);
  const [showInputError, setShowInputError] = useState(false);

  const [reloadData, setReloadData] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const requiredFields = ["make", "model", "year", "vin"];

  const getMakeList = useMemo(() => {
    const list = [...new Set(cars.map((car) => car.make))]
    if (formData.make && !list.includes(formData.make)) list.push(formData.make);
    return list.sort();
  }, [cars, formData.make]);

  function getModelList() {
    const list = cars
      .filter((c) => c.make === formData.make)
      .map((c) => c.model);
    if (formData.model && !list.includes(formData.model)) list.push(formData.model);
    return [...new Set(list)].sort();
  }

  function getYearList() {
    const list = cars
      .filter((c) => c.make === formData.make && c.model === formData.model)
      .map((c) => c.year);
    if (formData.year && !list.includes(formData.year)) list.push(formData.year);
    return [...new Set(list)].sort();
  }

  function updateFormData(e) {
    const { name, value } = e.target;
    const data = { ...formData };
    data[name] = value;
    setFormData(data);
  }

  function handleVinChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    debouncedVinLookup(e.target.value.trim());
  }

  const debouncedVinLookup = useCallback(
    debounce((vin) => {
      if (vin.length >= 10) fetchVehicleInfo(vin);
    }, 600),
    []
  );

  const fetchVehicleInfo = async (vin) => {
    try {
      const reqUrl = url + `/vin_details?vin=${encodeURIComponent(vin)}`;
      const token = window.localStorage.getItem("token");
      const headers = { authorization: `bearer ${token}` };
      const { data } = await axios.get(reqUrl, { headers });

      if (data.data.make && data.data.model && data.data.year) {
        setFormData((prev) => ({
          ...prev,
          make: data.data.make,
          model: data.data.model,
          year: data.data.year
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          make: "",
          model: "",
          year: ""
        }));
        toast.info("VIN details not found, please choose manually", { autoClose: 3000 });
      }

    } catch (err) {
      setFormData((prev) => ({
        ...prev,
        make: "",
        model: "",
        year: ""
      }));
    }
  };



  async function fetchCarData() {
    try {
      const reqUrl = url + `/fleet_customer_car/${viewData.id}`;
      const token = window.localStorage.getItem("token");
      const headers = { authorization: `bearer ${token}` };
      const { data } = await axios.get(reqUrl, { headers });
      setCarData(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message, {
        autoClose: 3000,
      });
    }
  }

  useEffect(() => {
    fetchCarData();
  }, [reloadData]);

  async function addFleetCar() {
    if (
      formData.make &&
      formData.model &&
      formData.year &&
      formData.vin &&
      !inputErrors.includes("error")
    ) {
      postData(
        `${url}/fleet_customer_car`,
        [{ ...formData, fleet_customer_id: viewData.id }],
        (error) => {
          toast.error(error.response.data.message, { autoClose: 3000 });
        },
        (data) => {
          setShowModal(false);
          setFormData({ make: "", model: "", year: "", vin: "", plate: "" });
          setReloadData((prev) => !prev);
          setMissingFields([]);
          setShowInputError(false);
          toast.success(data.message, { autoClose: 3000 });
        }
      );
    } else {
      let miss = [];
      requiredFields.forEach((field) => {
        if (!formData[field]) {
          miss.push(field);
        }
      });
      setMissingFields(miss);
      setShowInputError(true);
    }
  }

  async function addCar() {
    setShowModal(true);
  }

  async function handleDeleteCar() {
    try {
      const reqUrl = url + "/fleet_customer_car/" + deleteId;
      const token = window.localStorage.getItem("token");
      const headers = { authorization: `bearer ${token}` };
      const { data } = await axios.delete(reqUrl, { headers });
      toast.success(data.message, { autoClose: 3000 });
      setReloadData((prev) => !prev);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message, {
        autoClose: 3000,
      });
    } finally {
      setDeleteId(null);
    }
  }

  const downloadExcel = () => {
    const headers = [
      "vin",
      "make",
      "model",
      "year",
      "plate",
      "plate_state_province",
      "vehicle_number",
      "trim",
      "odo_unit",
      "current_odometer",
      "color",
      "engine",
      "transmission",
      "drivetrain",
      "tire_size_front",
      "tire_size_rear",
      "fuel_type",
      "ownership",
      "in_service_date",
      "warranty_expiration_date",
      "acquisition_cost",
      "location",
      "group",
      "notes",
      "custom_field_1",
      "custom_field_2",
      "custom_field_3"
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const colWidths = headers.map(() => ({ wch: 20 }));
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FleetCustomerCars");
    XLSX.writeFile(wb, "fleet_customer_car_template.xlsx");
  };

  function handleFileUpload(file) {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        if (jsonData.length == 0) {
          return toast.error("File is empty", { autoClose: 3000 })
        }
        if (jsonData.length > Number(importRowLimit)) {
          return toast.error(`File can only have upto ${importRowLimit} records`, { autoClose: 3000 })
        }

        postData(
          `${url}/import_fleet_car`,
          { data: jsonData, fleet_customer_id: viewData.id },
          (error) => {
            toast.error(error.response.data.message, { autoClose: 3000 });
          },
          (data) => {
            setReloadData((prev) => !prev);
            const { insertCount, updateCount, faildCount, errorResult } = data;
            toast.info(
              `Import Result\n` +
              `Record Created : ${insertCount}\n` +
              `Record Updated : ${updateCount}\n` +
              `Record Failed  : ${faildCount}`,
              {
                autoClose: 4000,
                style: { whiteSpace: "pre-line" },
              }
            );
            if (errorResult.length > 0) {
              const ws = XLSX.utils.json_to_sheet(errorResult);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, "Failed_Records");
              XLSX.writeFile(wb, "Import_Errors.xlsx");
            }
          }
        );

      } catch (err) {
        console.error("Error reading Excel file:", err);
      }
    };

    reader.onerror = (err) => {
      console.error("FileReader error:", err);
    };

    reader.readAsArrayBuffer(file);
  }

  return (
    <div className={modalContainer}>
      <div className='bg-white rounded-lg p-4 w-2/3'>
        <div className={`${modalTitle} mb-6`}>Manage Fleet</div>

        {deleteId && (
          <ConfirmationModal
            title={"Car"}
            setShowForm={setDeleteId}
            confirm={handleDeleteCar}
          />
        )}

        <div className='flex justify-end items-end mb-4 mr-2'>
          <Button
            className={`${btnOneNoWidth} mr-2`}
            clickHandler={() => setShowImportModal(true)}
            name='Import'
          />
          <Button
            className={`${btnOneNoWidth}`}
            clickHandler={() => addCar()}
            name='Add Car'
          />
        </div>

        {/* add car */}
        {showModal && (
          <div className={modalContainer}>
            <div className='bg-white rounded-lg p-4 w-2/3'>
              <div className={`${modalTitle} mb-6`}>Add Car </div>

              <div className='grid grid-cols-8 gap-4 py-1 px-2'>
                <div className='col-span-2'>
                  <label className={label}>Transmission</label>
                  <Select
                    index={12}
                    required={false}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name='transmission'
                    className={inputStyle}
                    placeHolder='Transmission'
                    onChange={(e) => updateFormData(e)}
                    options={['Auto', 'Manual', 'CVT']}
                  />
                </div>
                <div className='col-span-2'>
                  <label className={label}>Ownership</label>
                  <Select
                    index={17}
                    name='ownership'
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    required={false}
                    className={inputStyle}
                    placeHolder='Ownership'
                    onChange={(e) => updateFormData(e)}
                    options={['Owned', 'Leased', 'Rented']}
                  />
                </div>
                <div className='col-span-2'>
                  <label className={label}>Warranty Expiration Date</label>
                  <Input
                    index={18}
                    name='warranty_expiration_date'
                    type="date"
                    className={inputStyle}
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <label className={label}>In Service Date</label>
                  <Input
                    index={19}
                    name='in_service_date'
                    type="date"
                    className={inputStyle}
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
              </div>


              <div className='grid grid-cols-8 gap-4 py-1 px-2'>
                <div className='col-span-2'>
                  <Input
                    index={3}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name='vin'
                    required={true}
                    value={formData.vin}
                    className={inputStyle}
                    placeHolder='VIN'
                    onChange={handleVinChange}
                  />
                </div>
                <div className='col-span-2'>
                  <Select
                    index={0}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name='make'
                    type='text'
                    required={true}
                    value={formData.make}
                    className={`${inputStyle}`}
                    placeHolder='Make'
                    onChange={(e) => updateFormData(e)}
                    options={getMakeList}
                  />
                </div>
                <div className='col-span-2'>
                  <Select
                    index={1}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name='model'
                    type='text'
                    required={true}
                    value={formData.model}
                    className={inputStyle}
                    placeHolder='Model'
                    onChange={(e) => updateFormData(e)}
                    options={getModelList()}
                  />
                </div>
                <div className='col-span-2'>
                  <Select
                    index={2}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name='year'
                    type='year'
                    required={true}
                    value={formData.year}
                    className={inputStyle}
                    placeHolder='Year'
                    onChange={(e) => updateFormData(e)}
                    options={getYearList()}
                  />
                </div>
              </div>


              <div className='grid grid-cols-8 gap-4 py-1 px-2'>
                <div className='col-span-2'>
                  <Input
                    index={4}
                    name='plate_state_province'
                    className={inputStyle}
                    placeHolder='Plate State'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={5}
                    name='plate'
                    className={inputStyle}
                    placeHolder='Plate'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={6}
                    name='color'
                    className={inputStyle}
                    placeHolder='Color'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={7}
                    name='engine'
                    className={inputStyle}
                    placeHolder='Engine'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
              </div>


              <div className='grid grid-cols-8 gap-4 py-1 px-2'>
                <div className='col-span-2'>
                  <Input
                    index={8}
                    name='tire_size_front'
                    className={inputStyle}
                    placeHolder='Front Tire Size'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={9}
                    name='tire_size_rear'
                    className={inputStyle}
                    placeHolder='Rear Tire Size'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={10}
                    name='vehicle_number'
                    className={inputStyle}
                    placeHolder='Vehicle Number'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={11}
                    name='trim'
                    className={inputStyle}
                    placeHolder='Trim'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
              </div>


              <div className='grid grid-cols-8 gap-4 py-1 px-2'>
                <div className='col-span-2'>
                  <Select
                    index={16}
                    name='fuel_type'
                    required={false}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    className={inputStyle}
                    placeHolder='Fuel Type'
                    onChange={(e) => updateFormData(e)}
                    options={['Gasoline', 'Diesel', 'Electric', 'Hybrid', 'Other']}
                  />
                </div>
                <div className='col-span-2'>
                  <Select
                    index={13}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    required={false}
                    name='drivetrain'
                    className={inputStyle}
                    placeHolder='Drivetrain'
                    onChange={(e) => updateFormData(e)}
                    options={['FWD', 'RWD', 'AWD', '4WD']}
                  />
                </div>
                <div className='col-span-2'>
                  <Select
                    index={14}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name='odo_unit'
                    required={false}
                    className={inputStyle}
                    placeHolder='Odo Unit'
                    onChange={(e) => updateFormData(e)}
                    options={['mi', 'km']}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={15}
                    name='current_odometer'
                    type="number"
                    className={inputStyle}
                    placeHolder='Current Odometer'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
              </div>


              <div className='grid grid-cols-8 gap-4 py-1 px-2'>
                <div className='col-span-2'>
                  <Input
                    index={20}
                    name='notes'
                    className={inputStyle}
                    placeHolder='Notes'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={21}
                    name='group'
                    className={inputStyle}
                    placeHolder='group'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={22}
                    name='acquisition_cost'
                    type="number"
                    className={inputStyle}
                    placeHolder='Acquisition Cost'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
                <div className='col-span-2'>
                  <Input
                    index={23}
                    name='location'
                    className={inputStyle}
                    placeHolder='Location'
                    onChange={(e) => updateFormData(e)}
                  />
                </div>
              </div>


              <div className='flex justify-between py-3 px-16 gap-8'>
                <Button
                  clickHandler={(e) => { setMissingFields([]); setFormData({}); setShowInputError(false); setShowModal(false) }}
                  className={btnOne}
                  name={"Cancel"}
                />
                <Button
                  clickHandler={(e) => addFleetCar()}
                  className={btnOne}
                  name={"Submit"}
                />
              </div>
              <div className='text-red-500 font-semibold text-center'>
                {showInputError && "Please fill all the fields as directed!"}
              </div>
            </div>
          </div>
        )}

        {showImportModal &&
          <ImportModal setShowModal={setShowImportModal} downloadExcel={downloadExcel} handleFileUpload={handleFileUpload} type={"car"} importRowLimit={importRowLimit} />
        }

        {/* view car */}
        <div className='h-80 max-h-80 overflow-x-auto overflow-y-auto'>
          {carData.length === 0 ? (
            <div className='text-center text-gray-500'>No cars available</div>
          ) : (
            <div className="min-w-max">
              <table className='w-full border-collapse shadow-lg rounded-lg overflow-hidden'>
                <thead>
                  <tr className='bg-customTwo text-black font-semibold text-sm'>
                    <th className='px-4 py-2 border border-gray-300'>Make</th>
                    <th className='px-4 py-2 border border-gray-300'>Model</th>
                    <th className='px-4 py-2 border border-gray-300'>Year</th>
                    <th className='px-4 py-2 border border-gray-300'>VIN</th>
                    <th className='px-4 py-2 border border-gray-300'>Plate</th>
                    <th className="px-4 py-2 border border-gray-300">Color</th>
                    <th className="px-4 py-2 border border-gray-300">Plate State/Province</th>
                    <th className="px-4 py-2 border border-gray-300">Engine</th>
                    <th className="px-4 py-2 border border-gray-300">Transmission</th>
                    <th className="px-4 py-2 border border-gray-300">Drivetrain</th>
                    <th className="px-4 py-2 border border-gray-300">Tire Front</th>
                    <th className="px-4 py-2 border border-gray-300">Tire Rear</th>
                    <th className="px-4 py-2 border border-gray-300">Vehicle Number</th>
                    <th className="px-4 py-2 border border-gray-300">Trim</th>
                    <th className="px-4 py-2 border border-gray-300">Odo Unit</th>
                    <th className="px-4 py-2 border border-gray-300">Current Odo</th>
                    <th className="px-4 py-2 border border-gray-300">Fuel Type</th>
                    <th className="px-4 py-2 border border-gray-300">Ownership</th>
                    <th className="px-4 py-2 border border-gray-300">In-Service Date</th>
                    <th className="px-4 py-2 border border-gray-300">Warranty Expiration Date</th>
                    <th className="px-4 py-2 border border-gray-300">Acquisition Cost</th>
                    <th className="px-4 py-2 border border-gray-300">Location</th>
                    <th className='px-4 py-2 border border-gray-300'>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {carData.map((car, ind) => (
                    <tr
                      key={ind}
                      className={`text-center text-sm ${ind % 2 === 0 ? "bg-gray-50" : "bg-white"
                        }`}
                    >
                      <td className='px-4 py-2 border border-gray-200'>{car.make}</td>
                      <td className='px-4 py-2 border border-gray-200'>{car.model}</td>
                      <td className='px-4 py-2 border border-gray-200'>{car.year}</td>
                      <td className='px-4 py-2 border border-gray-200'>{car.vin}</td>
                      <td className='px-4 py-2 border border-gray-200'>{car.plate}</td>
                      <td className='px-4 py-2 border border-gray-200'>{car.color}</td>
                      <td className='px-4 py-2 border border-gray-200'>{car.plate_state_province}</td>
                      <td className='px-4 py-2 border border-gray-200'>{car.engine}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.transmission}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.drivetrain}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.tire_size_front}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.tire_size_rear}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.vehicle_number}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.trim}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.odo_unit}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.current_odometer}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.fuel_type}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.ownership}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.in_service_date && new Date(car.in_service_date).toLocaleDateString()}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.warranty_expiration_date && new Date(car.warranty_expiration_date).toLocaleDateString()}</td>
                      <td className="px-4 py-2 border border-gray-200">${formatNumberToUs(car.acquisition_cost)}</td>
                      <td className="px-4 py-2 border border-gray-200">{car.location}</td>
                      <td className='px-4 py-3 border border-gray-200 flex justify-center items-center'>
                        <MdDelete
                          className='text-red-500 cursor-pointer hover:scale-110 transition-transform duration-200'
                          onClick={() => setDeleteId(car.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className='flex justify-between px-40 mt-2'>
          <button onClick={() => setViewData(null)} className={btnCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddFleetCar;
